// ./src/components/generic/MapPinDrawer/MapPinDrawer.tsx
//
// A PIN ON A MAP, in a drawer (owner, 2026-10-23): find a place or an address, or click the map, drag the pin to
// fine-tune, and keep the matching address — editable ("North end of the park, by the fountain"). "Use my location" is
// there for when you stand at the spot, never the only way (a computer's location is a guess; an office is elsewhere).
// The picker itself is `@em/ui` MapPinPicker (the event website can host it too); this is the dashboard's drawer around
// it. ⓘ Local state, not a URL stage: it is opened from inside another drawer's form (a point's row), and gives its pin
// back to that form, which saves it.
// ⓘ Its users: a service point's "Where it is", a lost & found item's or report's spot (`LostFoundLocationField`). The
//   volunteer slots' CoordinatePickerDrawer is the same picker with a coverage radius and no stored address.
import React, { useEffect, useMemo, useState } from "react";
import { EMPTY_MAP_PIN, MapPin, MapPinPicker, MapPinPickerProps } from "@em/ui";

import { AppDrawer } from "@/components/generic/AppDrawer";
import { useFormSnapshot } from "@/hooks/useFormSnapshot";

export interface MapPinDrawerProps {
  opened: boolean;
  onClose: () => void;
  value: MapPin;
  /** The pin chosen (an empty one when the pin was removed). */
  onSave: (value: MapPin) => void;
  title?: string;
  /** Where the map opens without a pin (the venue, another point…). */
  center?: { latitude: number; longitude: number } | null;
  /** `edit` (default) — the address is kept with the pin and may be reworded; `show` — a hint only (see MapPinPicker). */
  addressMode?: MapPinPickerProps["addressMode"];
}

/** What a save would change: an address that is only SHOWN (looked up as the drawer opens) changes nothing. */
const snapshotOf = (pin: MapPin, addressMode: MapPinDrawerProps["addressMode"]) => ({
  latitude: pin.latitude,
  longitude: pin.longitude,
  accuracyM: pin.accuracyM ?? null,
  address: addressMode === "show" ? "" : pin.address,
});

export const MapPinDrawer: React.FC<MapPinDrawerProps> = ({ opened, onClose, value, onSave, title = "Where it is", center = null, addressMode = "edit" }) => {
  const [draft, setDraft] = useState<MapPin>(value);
  const compared = useMemo(() => snapshotOf(draft, addressMode), [draft, addressMode]);
  const { isDirty, seedFrom, clear } = useFormSnapshot(compared);

  // A fresh draft each time it opens (the row's current pin), and its snapshot; none while closed.
  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    setDraft(value);
    seedFrom(snapshotOf(value, addressMode));
    // Only on opening: the row does not change while this drawer is over it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const save = () => {
    const address = draft.address.trim();

    onSave(draft.latitude != null && draft.longitude != null ? { ...draft, address } : { ...EMPTY_MAP_PIN, address });
    onClose();
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title}
      position="right"
      size="xl"
      actionType="save-cancel"
      confirmLabel="Use this place"
      onConfirm={save}
      isDirty={isDirty}
      mobileHeightPercent={95}
    >
      {/* Mounted only while open: the map is made on mount and released on close. */}
      {opened ? <MapPinPicker value={draft} onChange={setDraft} center={center} height="min(55vh, 460px)" addressMode={addressMode} /> : null}
    </AppDrawer>
  );
};
